import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {CaptionEditableItemContext} from 'notebook/mediocre/base-plugin/CaptionEditableItemContext';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
// @ts-expect-error - SelUtil is an untyped JS module
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as keyboard from 'util/Keyboard';
import {ElementClipboardEvent, ElementKeyboardEvent} from 'util/Types';

import {GridMovementDirection} from './KeyboardNavigationConstants';
export interface GridItemWrapperProps {
  /** The cid of the note part. Used for the hidden focus editable's data-cid. */
  cid: string;
  /** Initial item width, typically from notePart.itemWidth. Updated externally on undo/redo or reorder. */
  initialItemWidth: number | undefined;
  /** Whether this item is currently selected in the grid. */
  isSelectedInGrid: boolean;
  /** Returns true and shows a lock warning if the note is locked. Used to gate keyboard mutations. */
  isLockedWithEditWarn: () => boolean;
  /** Store editor state before a mutation (for undo support). */
  storeState?: (args: {selection: BenchlingSelection} | null) => void;
  /** Restore editor state on undo/redo. */
  restoreState?: (e: ElementKeyboardEvent, selection: {selection: BenchlingSelection}) => void;
  /** Request the mediocre editor to set selection to a specific cid. */
  requestSelection?: (args: {selection: BenchlingSelection}) => void;
  /** Called when the item should be removed (delete/backspace or cut). */
  onDetach: () => void;
  /** Called to navigate to a different item in the grid */
  onMove: (direction: GridMovementDirection) => void;
  /** Called to set clipboard data on copy/cut. */
  onSetClipboard: (e: ElementClipboardEvent) => void;
  /** Called when the user pastes into a selected grid item. */
  onPaste?: (e: ElementClipboardEvent) => void;
  /** Render prop that receives itemWidth state and setter. */
  children: (args: {setItemWidth: (itemWidth: number) => void; itemWidth: number | undefined}) => JSX.Element;
}

/**
 * GridItemWrapper is a lightweight wrapper for items within a NotePartGrid.
 *
 * It provides several functionalities that grid items need:
 * - `itemWidth` state management (exposed via render prop)
 * - Keyboard event handling (delete, tab, arrows, copy/cut, undo/redo)
 * - A hidden focusable element for receiving keyboard events when selected
 */
export default function GridItemWrapper({
  cid,
  initialItemWidth,
  isSelectedInGrid,
  isLockedWithEditWarn,
  storeState,
  restoreState,
  requestSelection,
  onDetach,
  onMove,
  onSetClipboard,
  onPaste,
  children,
}: GridItemWrapperProps): JSX.Element {
  // ── State ──────────────────────────────────────────────────────────────────

  const [itemWidth, setItemWidthRaw] = useState<number | undefined>(initialItemWidth);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const hiddenEditableRef = useRef<HTMLDivElement>(null);

  // Floor the width to avoid sub-pixel rendering issues (matches CaptionEditable behavior)
  const setItemWidth = useCallback((width: number) => {
    setItemWidthRaw(Math.floor(width));
  }, []);

  // Sync itemWidth from props when the persisted width changes externally
  // (e.g., undo/redo, reorder, or selection change refreshing from source of truth).
  // This mirrors CaptionEditable's UNSAFE_componentWillReceiveProps behavior.
  useEffect(() => {
    setItemWidthRaw(initialItemWidth);
  }, [initialItemWidth]);

  // Auto-focus the hidden editable when this item becomes selected,
  // so keyboard events are captured immediately.
  useEffect(() => {
    if (isSelectedInGrid && hiddenEditableRef.current) {
      hiddenEditableRef.current.focus();
    }
  }, [isSelectedInGrid]);

  // ── Computed values ────────────────────────────────────────────────────────

  const contextValue = useMemo(() => ({isImageEditorOpen, setIsImageEditorOpen}), [isImageEditorOpen]);

  // ── Keyboard handling ──────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: ElementKeyboardEvent) => {
      // Let copy/cut propagate to the onCopy/onCut handlers
      if (keyboard.isCtrlX(e) || keyboard.isCtrlC(e)) {
        return;
      }

      // Let paste propagate to onPaste handler if it's defined
      if (onPaste !== undefined && keyboard.isCtrlV(e)) {
        return;
      }

      // Allow delete key through when image editor is open (for drawing deletion)
      if (keyboard.isADeleteKey(e) && isImageEditorOpen) {
        return;
      }

      // Prevent default browser behavior on the hidden contentEditable
      e.preventDefault();

      // Handle undo/redo
      if (keyboard.isUndoRedo(e)) {
        if (!isLockedWithEditWarn() && restoreState != null) {
          restoreState(e, {selection: selutil.getSelectionIndices()});
        }
        return;
      }

      // Gate destructive/navigational keys behind lock check
      if (['Backspace', 'Delete', 'Tab'].includes(e.key) && isLockedWithEditWarn()) {
        return;
      }

      switch (e.key) {
        case 'Backspace':
        case 'Delete':
          if (storeState != null) {
            storeState({selection: selutil.getSelectionIndices()});
          }
          onDetach();
          break;
        case 'Tab':
          if (storeState != null) {
            storeState({selection: selutil.getSelectionIndices()});
          }
          onMove(e.shiftKey ? GridMovementDirection.Left : GridMovementDirection.Right);
          break;
        case 'ArrowLeft':
          onMove(GridMovementDirection.Left);
          break;
        case 'ArrowUp':
          onMove(GridMovementDirection.Up);
          break;
        case 'ArrowRight':
          onMove(GridMovementDirection.Right);
          break;
        case 'ArrowDown':
          onMove(GridMovementDirection.Down);
          break;
        default:
          break;
      }
    },
    [isImageEditorOpen, isLockedWithEditWarn, restoreState, storeState, onDetach, onMove, onPaste]
  );

  const handleCopy = useCallback(
    (e: ElementClipboardEvent) => {
      e.preventDefault();
      onSetClipboard(e);
    },
    [onSetClipboard]
  );

  const handleCut = useCallback(
    (e: ElementClipboardEvent) => {
      e.preventDefault();
      if (isLockedWithEditWarn()) {
        return;
      }
      onSetClipboard(e);
      // Store null (not the current selection) before detaching, matching CaptionEditable's cut behavior
      if (storeState != null) {
        storeState(null);
      }
      onDetach();
    },
    [isLockedWithEditWarn, onSetClipboard, storeState, onDetach]
  );

  const handlePaste = useCallback(
    (e: ElementClipboardEvent) => {
      e.preventDefault();
      if (isLockedWithEditWarn()) {
        return;
      }
      onPaste?.(e);
    },
    [isLockedWithEditWarn, onPaste]
  );

  // ── Item click handling ─────────────────────────────────────────────────────

  // When the user clicks on the preview area (which has .mediocre-itemSelect),
  // notify the mediocre editor's selection system. This mirrors the behavior from
  // CaptionEditableItem's handleItemClick.
  const attachmentCid = `${cid}-item`;
  const handleMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      if ((e.target as HTMLElement).closest('.mediocre-itemSelect')) {
        e.preventDefault();
        if (requestSelection != null) {
          requestSelection({selection: {cid: attachmentCid, start: 0, end: 0}});
        }
      }
    },
    [requestSelection, attachmentCid]
  );

  return (
    <div className={isSelectedInGrid ? 'gridItemWrapper--selected' : undefined}>
      {/* Hidden focusable element for keyboard event handling.
          Only rendered when selected to prevent focus-based styling on unselected items. */}
      {isSelectedInGrid && (
        <div
          ref={hiddenEditableRef}
          className="hiddenFocusEditable editable"
          {...getTestProps({component: 'GridItemWrapper', element: 'hiddenFocusEditable'})}
          data-cid={attachmentCid}
          data-formattable={true}
          contentEditable={true}
          onCopy={handleCopy}
          onCut={handleCut}
          onPaste={onPaste ? handlePaste : undefined}
          onKeyDown={handleKeyDown}
        >
          {null}
        </div>
      )}
      <CaptionEditableItemContext.Provider value={contextValue}>
        <div className="note-item-wrapper" onMouseDown={handleMouseDown}>
          {children({setItemWidth, itemWidth})}
        </div>
      </CaptionEditableItemContext.Provider>
    </div>
  );
}
